import { useCallback, useRef, useState } from "react";
import { removeBackgroundViaApi, BackgroundRemovalMode } from "@/utils/image/removeBackground";
import { Image as ImageIcon, Scissors, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { extractColorPaletteFromFile, ExtractedColorPalette } from "@/utils/image/colorExtraction";
import { smartOptimizeImage } from "@/utils/image/optimization";
import { uploadImage } from "@/utils/image/upload";

interface EnhancedImageUploadProps {
  bucket: "product-images" | "genie-images";
  value?: string;
  onChange: (url: string | undefined) => void;
  onColorExtracted?: (colors: ExtractedColorPalette) => void;
  onUploadComplete?: (url: string, colors: ExtractedColorPalette | null) => void;
  onBeforeRemove?: (url: string) => boolean;
  label?: string;
  className?: string;
  showColorExtraction?: boolean;
  showBackgroundRemoval?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  squarePad?: boolean;
}

export const EnhancedImageUpload = ({
  bucket,
  value,
  onChange,
  onColorExtracted,
  onUploadComplete,
  onBeforeRemove,
  label = "Upload Image",
  className = "",
  showColorExtraction = false,
  showBackgroundRemoval = false,
  maxWidth = 500,
  maxHeight = 500,
  squarePad = false,
}: EnhancedImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [extractedColors, setExtractedColors] = useState<ExtractedColorPalette | null>(null);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [showBackgroundOptions, setShowBackgroundOptions] = useState(false);
  const [backgroundRemovalMode, setBackgroundRemovalMode] = useState<BackgroundRemovalMode>("standard");
  const [isResizing, setIsResizing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resizeImage = async (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height;

          if (width > height) {
            width = Math.min(width, maxWidth);
            height = width / aspectRatio;
          } else {
            height = Math.min(height, maxHeight);
            width = height * aspectRatio;
          }

          // Ensure we don't exceed max dimensions
          if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
          }
          if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
          }
        }

        if (squarePad) {
          const size = Math.max(width, height);
          canvas.width = size;
          canvas.height = size;
          ctx.clearRect(0, 0, size, size);
          ctx.drawImage(img, (size - width) / 2, (size - height) / 2, width, height);
        } else {
          canvas.width = width;
          canvas.height = height;
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
        }

        const needsTransparency = squarePad && img.width !== img.height;
        const outputType = needsTransparency ? "image/png" : file.type;
        const outputName = needsTransparency ? file.name.replace(/\.[^.]+$/, ".png") : file.name;

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], outputName, {
                type: outputType,
                lastModified: Date.now(),
              });

              const originalSize = (file.size / 1024).toFixed(0);
              const newSize = (resizedFile.size / 1024).toFixed(0);
              const reduction = (((file.size - resizedFile.size) / file.size) * 100).toFixed(0);

              if (file.size > resizedFile.size) {
                console.log(`Image resized from ${img.width}x${img.height} to ${width}x${height}`);
                toast.success(
                  `Image resized: ${originalSize}KB → ${newSize}KB (${reduction}% smaller)`,
                );
              }

              resolve(resizedFile);
            } else {
              resolve(file);
            }
          },
          outputType,
          0.9,
        );
      };

      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleBackgroundRemoval = async (file: File): Promise<File> => {
    console.log("Starting background removal for:", file.name, file.type, file.size);
    setIsRemovingBackground(true);

    try {
      toast.info("Processing image, this may take a moment...");
      const blob = await removeBackgroundViaApi(file, backgroundRemovalMode);

      console.log("Background removal successful, blob size:", blob.size);

      const removedBgFile = new File(
        [blob],
        file.name.replace(/\.(jpg|jpeg|png)$/i, "_no_bg.png"),
        { type: "image/png" },
      );

      console.log("Created new file:", removedBgFile.name, removedBgFile.type, removedBgFile.size);
      toast.success(`Background removed! File size: ${(removedBgFile.size / 1024).toFixed(0)}KB`);
      return removedBgFile;
    } catch (error) {
      console.error("Background removal failed:", error);
      toast.error(`Background removal failed: ${error.message || "Unknown error"}`);
      return file;
    } finally {
      setIsRemovingBackground(false);
    }
  };

  const handleFileSelect = async (file: File, removeBackgroundOption = false) => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    let fileToUpload = file;

    // 1. Resize image first (if it's an image and too large)
    if (file.type.startsWith("image/")) {
      try {
        setIsResizing(true);
        fileToUpload = await resizeImage(fileToUpload);
      } catch (resizeError) {
        console.error("Image resizing failed:", resizeError);
        // Continue with original file
      } finally {
        setIsResizing(false);
      }
    }

    // 2. Background removal (if requested)
    if (removeBackgroundOption && file.type.startsWith("image/")) {
      fileToUpload = await handleBackgroundRemoval(fileToUpload);
    }

    // 3. Optimize the image
    try {
      const optimized = await smartOptimizeImage(fileToUpload);
      if (optimized !== fileToUpload) {
        const sizeDiff = (((fileToUpload.size - optimized.size) / fileToUpload.size) * 100).toFixed(
          0,
        );
        toast.success(`Image optimized: ${sizeDiff}% smaller`);
        fileToUpload = new File([optimized], fileToUpload.name, { type: optimized.type });
      }
    } catch (optError) {
      console.error("Image optimization failed:", optError);
      // Continue with current file
    }

    // 4. Extract colors before upload (if callback provided)
    let colors: ExtractedColorPalette | null = null;
    try {
      if ((onColorExtracted || onUploadComplete) && file.type.startsWith("image/")) {
        colors = await extractColorPaletteFromFile(file);
        setExtractedColors(colors);
      }
    } catch (colorError) {
      console.error("Color extraction failed:", colorError);
      // Don't block upload if color extraction fails
    }

    // 5. Upload the image
    const result = await uploadImage(fileToUpload, bucket);

    if (result.error) {
      setError(result.error);
      setIsUploading(false);
      return;
    }

    // 6. Call the appropriate callback
    if (onUploadComplete) {
      // New combined callback - passes both URL and colors in one call
      onUploadComplete(result.url, colors);
    } else {
      // Legacy behavior - separate callbacks
      onChange(result.url);
      if (colors && onColorExtracted) {
        onColorExtracted(colors);
      }
    }

    setIsUploading(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log("File selected:", file?.name, "showBackgroundRemoval:", showBackgroundRemoval);

    if (file) {
      if (showBackgroundRemoval && file.type.startsWith("image/")) {
        console.log("Showing background removal options");
        setShowBackgroundOptions(true);
      } else {
        console.log("Processing without background removal");
        handleFileSelect(file, false);
      }
    }
  };

  const handleRemove = async () => {
    if (!value) return;

    setIsUploading(true);

    // Check if removal is allowed (e.g., don't delete team branding logo outside AccountBrandingPage)
    const shouldDeleteFromStorage = !onBeforeRemove || onBeforeRemove(value);

    // if (shouldDeleteFromStorage) {
    //   const success = await deleteImage(value, bucket);

    //   if (!success) {
    //     setError("Failed to remove image");
    //     setIsUploading(false);
    //     return;
    //   }
    // }

    onChange(undefined);
    setExtractedColors(null);
    setIsUploading(false);
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (imageFile) {
      if (showBackgroundRemoval) {
        setShowBackgroundOptions(true);
        // Store the file temporarily for the background removal dialog
        fileInputRef.current!.files = e.dataTransfer.files;
      } else {
        handleFileSelect(imageFile, false);
      }
    } else {
      setError("Please drop an image file");
    }
  }, []);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      {value ? (
        <div className="space-y-3">
          <div className="relative bg-gray-50 rounded-lg border border-gray-200 h-48 flex items-center justify-center">
            <img
              src={value}
              alt="Uploaded"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={isUploading}
              className="absolute top-2 right-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {showColorExtraction && extractedColors && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-medium text-gray-600 mb-2">Extracted Colors</p>
              <div className="flex gap-2">
                {extractedColors.palette.slice(0, 5).map((color, index) => (
                  <div key={index} className="relative group">
                    <div
                      className="w-8 h-8 rounded-md border border-gray-300 cursor-pointer hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {color}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Click on brand colors above to use these extracted colors
              </p>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all h-48 flex flex-col justify-center",
            isDragging
              ? "border-blue-500 bg-blue-50 scale-[1.02]"
              : "border-gray-300 hover:border-gray-400",
            (isUploading || isRemovingBackground || isResizing) && "opacity-50 cursor-not-allowed",
          )}
        >
          {isDragging ? (
            <>
              <Upload className="mx-auto h-12 w-12 text-blue-500 animate-bounce" />
              <p className="mt-2 text-sm font-medium text-blue-600">Drop your image here</p>
            </>
          ) : isResizing ? (
            <>
              <div className="relative mx-auto w-16 h-16 mb-4">
                <ImageIcon className="absolute inset-0 h-12 w-12 text-purple-600 m-auto animate-pulse" />
                <div className="absolute inset-0 border-2 border-purple-200 rounded-full animate-spin border-t-purple-600"></div>
              </div>
              <p className="mt-2 text-sm font-medium text-purple-600">Resizing image...</p>
              <p className="text-xs text-gray-500 mt-1">Optimizing dimensions</p>
              <div className="flex justify-center space-x-1 mt-2">
                <div
                  className="h-1.5 w-1.5 bg-purple-600 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                ></div>
                <div
                  className="h-1.5 w-1.5 bg-purple-600 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                ></div>
                <div
                  className="h-1.5 w-1.5 bg-purple-600 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                ></div>
              </div>
            </>
          ) : isRemovingBackground ? (
            <>
              <div className="relative mx-auto w-16 h-16 mb-4">
                <Scissors className="absolute inset-0 h-12 w-12 text-blue-600 m-auto animate-pulse" />
                <div className="absolute inset-0 border-2 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
              </div>
              <p className="mt-2 text-sm font-medium text-blue-600">Removing background...</p>
              <p className="text-xs text-gray-500 mt-1">This may take a moment</p>
              {/* Progress dots */}
              <div className="flex justify-center space-x-1 mt-2">
                <div
                  className="h-1.5 w-1.5 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                ></div>
                <div
                  className="h-1.5 w-1.5 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                ></div>
                <div
                  className="h-1.5 w-1.5 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                ></div>
              </div>
            </>
          ) : isUploading ? (
            <>
              <div className="relative mx-auto w-16 h-16 mb-4">
                <Upload className="absolute inset-0 h-12 w-12 text-green-600 m-auto animate-pulse" />
                <div className="absolute inset-0 border-2 border-green-200 rounded-full animate-spin border-t-green-600"></div>
              </div>
              <p className="mt-2 text-sm font-medium text-green-600">Uploading...</p>
              <div className="flex justify-center space-x-1 mt-2">
                <div
                  className="h-1.5 w-1.5 bg-green-600 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                ></div>
                <div
                  className="h-1.5 w-1.5 bg-green-600 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                ></div>
                <div
                  className="h-1.5 w-1.5 bg-green-600 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                ></div>
              </div>
            </>
          ) : (
            <>
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400 transition-transform hover:scale-110" />
              <p className="mt-2 text-sm text-gray-600">Click or drag to upload image</p>
              <p className="text-xs text-gray-500 mt-1">JPEG, PNG, WebP, GIF up to 5MB</p>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleInputChange}
        className="hidden"
        disabled={isUploading || isRemovingBackground || isResizing}
      />

      {/* Background Removal Options Dialog */}
      {showBackgroundOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 animate-in slide-in-from-bottom-4 duration-300">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Scissors className="h-5 w-5 mr-2 text-blue-600" />
              Remove Background
            </h3>

            {isRemovingBackground ? (
              <div className="flex flex-col items-center py-6 space-y-3">
                <div className="relative">
                  <Scissors className="h-10 w-10 text-blue-600 animate-pulse" />
                  <div className="absolute -inset-2 border-2 border-blue-200 rounded-full animate-spin border-t-blue-600" />
                </div>
                <p className="text-sm font-medium text-gray-900">Removing background...</p>
                <p className="text-xs text-gray-500">This may take a moment</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">Choose a removal mode:</p>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <button
                    onClick={() => setBackgroundRemovalMode("standard")}
                    className={cn(
                      "flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors",
                      backgroundRemovalMode === "standard"
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300",
                    )}
                  >
                    <span className="text-sm font-medium">Standard</span>
                    <span className="text-xs text-gray-500">Best for photos and product images</span>
                  </button>
                  <button
                    onClick={() => setBackgroundRemovalMode("advanced")}
                    className={cn(
                      "flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors",
                      backgroundRemovalMode === "advanced"
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300",
                    )}
                  >
                    <span className="text-sm font-medium">Advanced</span>
                    <span className="text-xs text-gray-500">Better for logos with text or thin strokes</span>
                  </button>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      const file = fileInputRef.current?.files?.[0];
                      if (file) handleFileSelect(file, true);
                      setShowBackgroundOptions(false);
                    }}
                    className="flex-1"
                  >
                    <Scissors className="h-4 w-4 mr-2" />
                    Remove Background
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const file = fileInputRef.current?.files?.[0];
                      if (file) handleFileSelect(file, false);
                      setShowBackgroundOptions(false);
                    }}
                    className="flex-1"
                  >
                    Keep Original
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setShowBackgroundOptions(false)}
                  className="w-full mt-2"
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
